import { todayInZone } from "@/components/autopilot/localDate";

/**
 * The once-a-day login claim (POST /api/users/me/login-xp): it awards the
 * daily login XP and advances the LOGIN streak (shown as "Login streak" on
 * Progress → XP). It is NOT what makes a day count on Progress → "Your days"
 * or the My Day flame — those count only days one of your own habits was
 * checked off (founder, 2026-10-12/13; backend services/app_days.py). The
 * launch funnel's "came back on day 2" still reads this claim as a visit.
 *
 * Fires now and again on every return to the foreground (2026-10-06): an
 * iPhone app resumed from the background doesn't remount, so a claim made
 * only on mount skipped every day the app was reopened from the switcher —
 * streaks reset and those days showed as missed. `visibilitychange` fires in
 * the Capacitor WebView on resume and in browsers on tab focus.
 *
 * A per-user-per-local-day localStorage flag keeps it to one request a day;
 * the backend dedupes anyway. Returns the cleanup for the listener.
 */
const claimsInFlight = new Set<string>();

export function startLoginClaims({ userId, tz, post, onClaimed }: {
  userId: string;
  tz: string | null;
  post: () => Promise<unknown>;
  onClaimed?: () => void;
}): () => void {
  const claim = () => {
    // The account's local date, not the UTC one: `toISOString()` flipped to
    // "tomorrow" at 5pm in Los Angeles, so the flag set by an evening open
    // still matched the next morning and that day's claim never went out
    // (leaderboard audit 2026-10-03).
    const todayKey = todayInZone(tz);
    const storageKey = `trybeup_login_claim_${userId}`;
    try {
      if (localStorage.getItem(storageKey) === todayKey) return;
    } catch {
      // localStorage blocked (Safari private mode, etc.) — call anyway; the
      // backend dedupes.
    }
    // Shared across every startLoginClaims caller (each useAuth() instance
    // starts one): a per-call flag let ~17 instances POST at once when the
    // endpoint failed (review 2026-10-15).
    if (claimsInFlight.has(userId)) return;
    claimsInFlight.add(userId);
    post()
      .then(() => {
        try { localStorage.setItem(storageKey, todayKey); } catch { /* ignore */ }
        onClaimed?.();
      })
      .catch(() => { /* non-critical; retried on the next foreground */ })
      .finally(() => { claimsInFlight.delete(userId); });
  };
  claim();
  const onVisible = () => { if (document.visibilityState === "visible") claim(); };
  document.addEventListener("visibilitychange", onVisible);
  return () => document.removeEventListener("visibilitychange", onVisible);
}
